import {
  Card,
  CardBody,
  CloseButton,
  DataList,
  Drawer,
  Grid,
  GridItem,
  IconButton,
  Image,
  Text,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineInformationCircle } from 'react-icons/hi';

import { type ActionSuggestionPublic, type IndicatorSuggestionPublic, LibraryService } from '@/client/index.ts';
import { Tooltip } from '@/components/ui/tooltip';
import { Route } from '@/routes/_layout/$slug/library.tsx';

import LibraryAddIndicatorDialog from './LibraryAddIndicatorDialog';
import { category } from './LibraryData';

type LibrarySuggestionDrawerProps = {
  content?: IndicatorSuggestionPublic | ActionSuggestionPublic;
  isOpen: boolean;
  setIsOpen: (state: boolean) => void;
};

function getLibrarySuggestionDetailsQueryOptions({
  type,
  slug,
  id,
}: {
  type: string;
  slug: string;
  id: string | undefined;
}) {
  return {
    queryKey: ['suggestionDetails', { slug, id }],
    queryFn: async () => {
      const _read = type === 'actions' ? LibraryService.readLibraryAction : LibraryService.readLibraryIndicator;
      if (id)
        return await _read({
          id,
          companySlug: slug,
        });
      return null;
    },
  };
}

const LibrarySuggestionDrawer = ({ content, isOpen, setIsOpen }: LibrarySuggestionDrawerProps) => {
  const { type } = Route.useSearch();
  const { t, i18n } = useTranslation();
  const { slug } = useParams({ strict: false });

  const { data: suggestionData, refetch } = useQuery({
    ...getLibrarySuggestionDetailsQueryOptions({
      type,
      slug,
      id: content?.id,
    }),
  });

  useEffect(() => {
    if (content?.id) refetch();
  }, [content?.id]);

  return (
    <Drawer.Root open={isOpen} onOpenChange={(e) => setIsOpen(e.open)} size={{ md: 'lg', base: 'md' }} placement="end">
      <Drawer.Backdrop />
      <Drawer.Content
        rounded="md"
        position={'absolute'}
        margin={'{spacing.4}'}
        h={'calc(100% - {spacing.4} * 2)'}
        right={0}
        top={0}
      >
        <Drawer.CloseTrigger asChild>
          <CloseButton size="sm" mt="2" mr="4" />
        </Drawer.CloseTrigger>
        <Drawer.Header display="flex" justifyContent="space-between" alignItems="center">
          <Text textStyle={'xl'} fontWeight={'bold'}>
            {t(`suggestion.drawer.${type}.details.title`)}
          </Text>
        </Drawer.Header>
        <Drawer.Body pb={20}>
          {suggestionData ? (
            <DataList.Root orientation="horizontal" divideY="1px" mb={4}>
              <DataList.Item>
                <DataList.ItemLabel>{t('suggestion.columnsHeader.name')}</DataList.ItemLabel>
                <DataList.ItemValue>
                  {suggestionData.name ?? (
                    <Text color="gray.500" textStyle="sm">
                      N/R
                    </Text>
                  )}
                </DataList.ItemValue>
              </DataList.Item>
              <DataList.Item pt="4">
                <DataList.ItemLabel>{t('suggestion.columnsHeader.commitmentName')}</DataList.ItemLabel>
                <DataList.ItemValue>
                  {suggestionData.commitment_suggestion_name ?? (
                    <Text color="gray.500" textStyle="sm">
                      N/R
                    </Text>
                  )}
                </DataList.ItemValue>
              </DataList.Item>
              <DataList.Item pt="4">
                <DataList.ItemLabel>{t('suggestion.columnsHeader.pillarName')}</DataList.ItemLabel>
                <DataList.ItemValue>
                  {suggestionData.pillar_suggestion_name ?? (
                    <Text color="gray.500" textStyle="sm">
                      N/R
                    </Text>
                  )}
                </DataList.ItemValue>
              </DataList.Item>
              <DataList.Item pt="4">
                <DataList.ItemLabel>{t('suggestion.columnsHeader.description')}</DataList.ItemLabel>
                <DataList.ItemValue>
                  {suggestionData.description ?? (
                    <Text color="gray.500" textStyle="sm">
                      N/R
                    </Text>
                  )}
                </DataList.ItemValue>
              </DataList.Item>
              {'providers' in suggestionData && (
                <DataList.Item pt="4">
                  <DataList.ItemLabel>{t('suggestion.columnsHeader.providers')}</DataList.ItemLabel>
                  <DataList.ItemValue>
                    {suggestionData.providers ?? (
                      <Text color="gray.500" textStyle="sm">
                        N/R
                      </Text>
                    )}
                  </DataList.ItemValue>
                </DataList.Item>
              )}
              {'aids' in suggestionData && (
                <DataList.Item pt="4">
                  <DataList.ItemLabel>{t('suggestion.columnsHeader.aids')}</DataList.ItemLabel>
                  <DataList.ItemValue>
                    {suggestionData.aids ?? (
                      <Text color="gray.500" textStyle="sm">
                        N/R
                      </Text>
                    )}
                  </DataList.ItemValue>
                </DataList.Item>
              )}
              {'lt_indicator_category' in suggestionData && (
                <DataList.Item pt="4">
                  <DataList.ItemLabel>Category</DataList.ItemLabel>
                  <DataList.ItemValue>
                    {(category.items.find((item) => item.value === suggestionData.lt_indicator_category)?.label ||
                      '') ?? (
                      <Text color="gray.500" textStyle="sm">
                        N/R
                      </Text>
                    )}
                  </DataList.ItemValue>
                </DataList.Item>
              )}
              {'lt_indicator_category' in suggestionData && suggestionData.lt_indicator_category === 'composite' && (
                <>
                  <DataList.Item pt="4">
                    <DataList.ItemLabel>Formula</DataList.ItemLabel>
                    <DataList.ItemValue>
                      {suggestionData.formula ?? (
                        <Text color="gray.500" textStyle="sm">
                          N/R
                        </Text>
                      )}
                    </DataList.ItemValue>
                  </DataList.Item>
                  <DataList.Item pt="4">
                    <DataList.ItemLabel>Formula unit(s)</DataList.ItemLabel>
                    <DataList.ItemValue>
                      {suggestionData.formula_units ?? (
                        <Text color="gray.500" textStyle="sm">
                          N/R
                        </Text>
                      )}
                    </DataList.ItemValue>
                  </DataList.Item>
                </>
              )}
              {'lt_indicator_category' in suggestionData &&
                'unit' in suggestionData &&
                suggestionData.lt_indicator_category !== 'composite' && (
                  <DataList.Item pt="4">
                    <DataList.ItemLabel>{t('suggestion.columnsHeader.unit')}</DataList.ItemLabel>
                    <DataList.ItemValue>
                      {suggestionData.lt_indicator_other_unit ?? (
                        <Text color="gray.500" textStyle="sm">
                          N/R
                        </Text>
                      )}
                    </DataList.ItemValue>
                  </DataList.Item>
                )}
              <DataList.Item pt="4">
                <DataList.ItemLabel>
                  {t('suggestion.columnsHeader.sdgs')}
                  <Tooltip
                    showArrow
                    openDelay={100}
                    closeDelay={100}
                    contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
                    content={
                      'The Sustainable Development Goals or Global Goals are a collection of 17 interlinked goals designed to be a “blueprint to achieve a better and more sustainable futurefor all”.'
                    }
                  >
                    <IconButton variant="ghost" aria-label="info" size="2xs" colorPalette="gray">
                      <HiOutlineInformationCircle />
                    </IconButton>
                  </Tooltip>
                </DataList.ItemLabel>
                <DataList.ItemValue>
                  <Grid
                    templateColumns={{
                      md: 'repeat(6, 1fr)',
                      base: 'repeat(4, 1fr)',
                    }}
                    gap="4"
                  >
                    {suggestionData.sdgs
                      .sort((a, b) => Number(a.code) - Number(b.code))
                      .map((sdg) => (
                        <GridItem key={sdg.code}>
                          <Image
                            rounded="md"
                            src={`/assets/images/sdgs/${i18n.language}/sdg_${sdg.code}.png`}
                            alt={sdg.name}
                            w={80}
                          />
                        </GridItem>
                      ))}
                    {suggestionData.sdgs.length === 0 && (
                      <Text color="gray.500" textStyle="sm">
                        N/R
                      </Text>
                    )}
                  </Grid>
                </DataList.ItemValue>
              </DataList.Item>
            </DataList.Root>
          ) : (
            <Card.Root>
              <CardBody>{t('suggestion.noSelection')}</CardBody>
            </Card.Root>
          )}
        </Drawer.Body>
        <Drawer.Footer width={'100%'} justifyContent={'space-between'} alignItems={'space-around'}>
          <Text color="gray.500" pt={2}>
            {suggestionData?.updated_at && (
              <>
                {t('suggestion.columnsHeader.updatedAt')} :{' '}
                {new Date(suggestionData?.updated_at).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </>
            )}
          </Text>
          {type === 'indicators' && (
            <LibraryAddIndicatorDialog suggestion={suggestionData} onSuccess={() => setIsOpen(false)} />
          )}
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer.Root>
  );
};

export default LibrarySuggestionDrawer;
